<template>
  <v-dialog :value="value" max-width="720" persistent scrollable @input="onDialogInput">
    <v-card class="launch-folder-run-dialog">
      <v-card-title class="px-6">
        <h5 class="primary-color mb-0">Launch run</h5>
        <v-spacer />
        <v-btn icon aria-label="Close" :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="px-6 pt-1">
        One folder, one configuration and the engine version deployed right now, measured together.
        A run never changes the analysis users see: results are recorded, not promoted.
      </v-card-subtitle>

      <v-card-text class="px-6 py-2">
        <v-alert v-if="loadError" dense text type="warning" class="mb-4">
          <div class="d-flex align-center">
            <span>{{ loadError }}</span>
            <v-spacer />
            <v-btn small outlined color="warning" @click="loadReferenceData">Retry</v-btn>
          </div>
        </v-alert>

        <div class="launch-folder-run-dialog__section">Folder</div>
        <v-select
          v-model="form.folderId"
          label="Folder *"
          :items="folders"
          item-text="name"
          item-value="id"
          :loading="loadingFolders"
          :disabled="saving || loadingFolders"
          :rules="[(v) => !!v || 'Select a folder']"
          dense
          outlined
          hide-details="auto"
          class="mb-4 launch-folder-run-dialog__folder"
          attach
        />

        <div class="launch-folder-run-dialog__section">Configuration</div>
        <v-select
          v-model="form.configId"
          label="Analysis configuration"
          :items="configItems"
          item-text="name"
          item-value="id"
          :loading="loadingConfigs"
          :disabled="saving || loadingConfigs"
          dense
          outlined
          hide-details="auto"
          class="mb-3 launch-folder-run-dialog__config"
          attach
        />
        <v-progress-linear v-if="loadingVersions" indeterminate color="primary" class="mb-2" />
        <v-radio-group
          v-if="form.configId"
          v-model="form.configVersionId"
          dense
          hide-details
          class="mt-0 mb-4 launch-folder-run-dialog__versions"
          :disabled="saving"
        >
          <v-radio :value="null" label="Current version (whatever is live when the run starts)" />
          <v-radio
            v-for="version in versions"
            :key="version.id"
            :value="version.id"
            :label="versionLabel(version)"
          />
        </v-radio-group>

        <div class="launch-folder-run-dialog__section">Engines</div>
        <EngineSelector
          v-model="form.engines"
          source-type="manual_analysis"
          :disabled="saving"
          default-all
          @change="onEnginesChange"
          @catalogue-loaded="onCatalogueLoaded"
        />
        <p
          v-if="showEnginesError"
          class="error--text caption mb-2 launch-folder-run-dialog__engines-error"
          role="alert"
        >
          Select at least one engine.
        </p>
        <p v-if="selection.auto_added.length" class="caption primary--text mt-2 mb-3">
          Added automatically: {{ selection.auto_added.map(labelOf).join(', ') }}
        </p>

        <div class="launch-folder-run-dialog__section mt-2">Identification</div>
        <v-text-field
          v-model="form.name"
          label="Name"
          placeholder="Baseline · gestures 2.1.0"
          dense
          outlined
          counter="160"
          maxlength="160"
          hide-details="auto"
          class="mb-3 launch-folder-run-dialog__name"
          :disabled="saving"
        />
        <v-textarea
          v-model="form.note"
          label="Note"
          placeholder="What is this run meant to answer?"
          rows="2"
          auto-grow
          dense
          outlined
          hide-details="auto"
          class="mb-2 launch-folder-run-dialog__note"
          :disabled="saving"
        />

        <v-alert v-if="error" dense text type="error" class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <span class="caption grey--text launch-folder-run-dialog__summary">{{ summaryLine }}</span>
        <v-spacer />
        <v-btn text class="cancel-modal" :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="saving"
          :disabled="!canLaunch"
          class="launch-folder-run-dialog__submit"
          @click="launch"
        >
          <v-icon small left>mdi-play</v-icon>
          Launch run
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import EngineSelector from '@/components/analysis/EngineSelector.vue';
import FolderRunsService from '@/services/FolderRunsService';
import InterviewsDataService from '@/services/InterviewsDataService';
import ManualAnalysisService from '@/services/ManualAnalysisService';
import { engineLabel, formatLaunchSummary } from '@/utils/engineRules';
import { describeLaunchError, formatDateTime } from '@/utils/folderRuns';

const EMPTY_SELECTION = Object.freeze({
  engines: [],
  requested: [],
  auto_added: [],
  warnings: [],
  valid: false,
  error: 'engines_empty',
});

/** Sentinel for "let the backend pick the folder's own configuration" (contract POST /). */
const FOLDER_DEFAULT_CONFIG = null;

/**
 * Launch dialog of /runs: folder, configuration, configuration version, engines, name and note.
 *
 * Emits `launched` with the 202 body `{ id, batch, accepted, rejected }` so the list can jump
 * straight to the new run and start polling it.
 */
export default {
  name: 'LaunchFolderRunDialog',

  components: { EngineSelector },

  props: {
    value: { type: Boolean, default: false },
    /** Pre-selected folder id (used by "Run this folder again" entry points). */
    folderId: { type: [Number, String], default: null },
  },

  data() {
    return {
      form: {
        folderId: null,
        configId: FOLDER_DEFAULT_CONFIG,
        configVersionId: null,
        engines: [],
        name: '',
        note: '',
      },
      folders: [],
      configs: [],
      versions: [],
      catalogue: [],
      selection: EMPTY_SELECTION,
      loadingFolders: false,
      loadingConfigs: false,
      loadingVersions: false,
      loadError: '',
      saving: false,
      error: '',
      touched: false,
    };
  },

  computed: {
    /** The first entry keeps the backend's default: the folder's own configuration. */
    configItems() {
      return [{ id: FOLDER_DEFAULT_CONFIG, name: "The folder's configuration" }, ...this.configs];
    },
    selectedConfig() {
      return this.configs.find((item) => Number(item.id) === Number(this.form.configId)) || null;
    },
    selectedVersion() {
      if (!this.form.configVersionId) return null;
      return this.versions.find((version) => version.id === this.form.configVersionId) || null;
    },
    showEnginesError() {
      return this.touched && !this.selection.valid;
    },
    canLaunch() {
      return !this.saving && Boolean(this.form.folderId) && this.selection.valid;
    },
    summaryLine() {
      return formatLaunchSummary({
        engines: this.selection.engines,
        configName: this.selectedConfig?.name || null,
        configVersion: this.selectedVersion?.version ?? null,
      });
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
    'form.configId'(id, previous) {
      if (previous !== undefined && id !== previous) this.form.configVersionId = null;
      if (id) this.loadVersions(id);
      else this.versions = [];
    },
  },

  created() {
    if (this.value) this.reset();
  },

  methods: {
    reset() {
      this.saving = false;
      this.error = '';
      this.touched = false;
      this.selection = EMPTY_SELECTION;
      this.form = {
        folderId: this.folderId != null ? Number(this.folderId) : null,
        configId: FOLDER_DEFAULT_CONFIG,
        configVersionId: null,
        engines: [],
        name: '',
        note: '',
      };
      this.loadReferenceData();
    },

    async loadReferenceData() {
      this.loadError = '';
      await Promise.all([this.loadFolders(), this.loadConfigs()]);
    },

    async loadFolders() {
      this.loadingFolders = true;
      try {
        const response = await ManualAnalysisService.getFolders();
        const items = response?.data?.data ?? response?.data;
        this.folders = Array.isArray(items) ? items : [];
        if (this.form.folderId == null && this.folders.length === 1) {
          this.form.folderId = this.folders[0].id;
        }
      } catch (error) {
        this.folders = [];
        this.loadError = 'Could not load the folders.';
      } finally {
        this.loadingFolders = false;
      }
    },

    async loadConfigs() {
      this.loadingConfigs = true;
      try {
        const response = await InterviewsDataService.findAllInterviewAnalysisTypes(1, 100);
        const items = response?.data?.data;
        this.configs = Array.isArray(items) ? items : [];
      } catch (error) {
        this.configs = [];
        this.loadError = 'Could not load the analysis configurations.';
      } finally {
        this.loadingConfigs = false;
      }
    },

    async loadVersions(id) {
      this.loadingVersions = true;
      try {
        const response = await InterviewsDataService.getConfigVersions(id);
        const items = response?.data?.data ?? response?.data;
        this.versions = Array.isArray(items) ? items : [];
      } catch (error) {
        this.versions = [];
      } finally {
        this.loadingVersions = false;
      }
    },

    versionLabel(version) {
      const parts = [`v${version.version}`];
      if (version.created_at) parts.push(formatDateTime(version.created_at));
      if (version.notes) parts.push(version.notes);
      return parts.join(' · ');
    },

    labelOf(name) {
      return engineLabel(this.catalogue, name);
    },

    onEnginesChange(result) {
      this.selection = result || EMPTY_SELECTION;
      this.touched = this.touched || Boolean(result && result.requested && result.requested.length);
    },

    onCatalogueLoaded(catalogue) {
      this.catalogue = Array.isArray(catalogue) ? catalogue : [];
    },

    onDialogInput(open) {
      if (!open) this.close();
    },

    close() {
      if (this.saving) return;
      this.$emit('input', false);
    },

    async launch() {
      this.touched = true;
      if (!this.canLaunch) return;
      this.saving = true;
      this.error = '';
      try {
        const response = await FolderRunsService.create({
          folder_id: this.form.folderId,
          config_id: this.form.configId ?? undefined,
          config_version_id: this.form.configVersionId,
          engines: this.selection.requested,
          name: this.form.name,
          note: this.form.note,
          socketId: this.$socketId,
        });
        const body = response?.data?.data ?? response?.data ?? {};
        this.$emit('launched', {
          id: body.id ?? null,
          batch: body.batch ?? null,
          accepted: Array.isArray(body.accepted) ? body.accepted : [],
          rejected: Array.isArray(body.rejected) ? body.rejected : [],
        });
        this.$emit('input', false);
      } catch (error) {
        this.error = describeLaunchError(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.launch-folder-run-dialog__section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #757575;
  margin-bottom: 8px;
}
.launch-folder-run-dialog__summary {
  flex: 1 1 220px;
  min-width: 0;
  overflow-wrap: anywhere;
}
</style>
